'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'login' | 'signup';
  defaultPlan?: string;
  showToast: (msg: string) => void;
}

export default function AuthModal({ isOpen, onClose, defaultTab = 'login', defaultPlan = 'Pro', showToast }: AuthModalProps) {
  const [tab, setTab] = useState<'login' | 'signup'>(defaultTab);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupCompany, setSignupCompany] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { signIn, signUp } = useAuth();
  const router = useRouter();

  if (!isOpen) return null;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');
    try {
      await signIn(loginEmail, loginPassword);
      onClose();
      showToast('Innlogging vellykket — velkommen tilbake!');
      router.push('/dashboard');
    } catch (err: any) {
      setErrorMsg(err?.message || 'Innlogging mislyktes. Sjekk e-post og passord.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');
    try {
      await signUp(signupEmail, signupPassword, signupName, signupCompany, signupPhone, emailOptIn, smsOptIn);
      onClose();
      showToast('Konto opprettet! Sjekk telefonen for en velkomst-SMS.');
      router.push('/onboarding');
    } catch (err: any) {
      setErrorMsg(err?.message || 'Registrering mislyktes. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleStyle = (active: boolean) => ({
    position: 'relative' as const,
    width: '48px',
    height: '24px',
    border: `1px solid ${active ? 'white' : 'rgba(255,255,255,0.3)'}`,
    background: active ? 'white' : 'transparent',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.2s ease',
  });

  const thumbStyle = (active: boolean) => ({
    position: 'absolute' as const,
    top: '3px',
    left: active ? 'calc(100% - 18px)' : '3px',
    width: '16px',
    height: '16px',
    background: active ? 'black' : 'rgba(255,255,255,0.5)',
    transition: 'left 0.2s ease',
  });

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        style={{
          background: '#0A0A0A',
          border: '1px solid #2A2A2A',
          width: '100%',
          maxWidth: '480px',
          padding: '48px',
          position: 'relative',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        className="auth-modal-inner"
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '24px',
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.4)',
            fontSize: '1.2rem',
            cursor: 'pointer',
            fontFamily: 'Rajdhani, sans-serif',
          }}
        >
          ✕
        </button>

        {/* Logo */}
        <div style={{ marginBottom: '32px' }}>
          <p
            style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 700,
              fontSize: '1.4rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'white',
            }}
          >
            SebNorge
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #2A2A2A', marginBottom: '32px' }}>
          {(['login', 'signup'] as const).map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setErrorMsg(''); }}
              style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                padding: '12px 24px',
                background: 'none',
                border: 'none',
                borderBottom: tab === t ? '2px solid white' : '2px solid transparent',
                color: tab === t ? 'white' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                marginBottom: '-1px',
                transition: 'color 0.2s ease',
              }}
            >
              {t === 'login' ? 'Logg inn' : 'Opprett konto'}
            </button>
          ))}
        </div>

        {/* Error message */}
        {errorMsg && (
          <div
            style={{
              background: 'rgba(255,60,60,0.1)',
              border: '1px solid rgba(255,60,60,0.3)',
              color: '#ff6b6b',
              fontFamily: 'Nunito Sans, sans-serif',
              fontSize: '0.82rem',
              padding: '12px 16px',
              marginBottom: '20px',
            }}
          >
            {errorMsg}
          </div>
        )}

        {/* Login Form */}
        {tab === 'login' && (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
              <label className="section-label" style={{ display: 'block', marginBottom: '8px' }}>E-postadresse</label>
              <input
                type="email"
                className="input-line"
                placeholder="din@epost.no"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="section-label" style={{ display: 'block', marginBottom: '8px' }}>Passord</label>
              <input
                type="password"
                className="input-line"
                placeholder="••••••••"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn-full-white" style={{ marginTop: '8px' }} disabled={isLoading}>
              {isLoading ? 'Logger inn...' : 'Logg inn'}
            </button>
            <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
              Ingen konto?{' '}
              <button
                type="button"
                onClick={() => { setTab('signup'); setErrorMsg(''); }}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit', fontSize: 'inherit' }}
              >
                Opprett konto
              </button>
            </p>
          </form>
        )}

        {/* Signup Form */}
        {tab === 'signup' && (
          <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
              <label className="section-label" style={{ display: 'block', marginBottom: '8px' }}>Fullt navn</label>
              <input
                type="text"
                className="input-line"
                placeholder="Ola Nordmann"
                value={signupName}
                onChange={(e) => setSignupName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="section-label" style={{ display: 'block', marginBottom: '8px' }}>E-postadresse</label>
              <input
                type="email"
                className="input-line"
                placeholder="din@epost.no"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="section-label" style={{ display: 'block', marginBottom: '8px' }}>Bedrift (valgfritt)</label>
              <input
                type="text"
                className="input-line"
                placeholder="Bedriftsnavn AS"
                value={signupCompany}
                onChange={(e) => setSignupCompany(e.target.value)}
              />
            </div>
            <div>
              <label className="section-label" style={{ display: 'block', marginBottom: '8px' }}>Passord</label>
              <input
                type="password"
                className="input-line"
                placeholder="••••••••"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            {/* Phone number field */}
            <div>
              <label className="section-label" style={{ display: 'block', marginBottom: '8px' }}>Mobilnummer</label>
              <input
                type="tel"
                className="input-line"
                placeholder="+47 000 00 000"
                value={signupPhone}
                onChange={(e) => setSignupPhone(e.target.value)}
                required
              />
            </div>

            {/* Opt-in toggles */}
            <div style={{ display: 'flex', gap: '24px' }}>
              {/* E-post varsler */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                <button
                  type="button"
                  onClick={() => setEmailOptIn(!emailOptIn)}
                  style={toggleStyle(emailOptIn)}
                  aria-label="Toggle e-post varsler"
                >
                  <span style={thumbStyle(emailOptIn)} />
                </button>
                <span
                  style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: emailOptIn ? 'white' : 'rgba(255,255,255,0.4)',
                  }}
                >
                  E-post varsler
                </span>
              </div>
              {/* SMS varsler */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                <button
                  type="button"
                  onClick={() => setSmsOptIn(!smsOptIn)}
                  style={toggleStyle(smsOptIn)}
                  aria-label="Toggle SMS varsler"
                >
                  <span style={thumbStyle(smsOptIn)} />
                </button>
                <span
                  style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: smsOptIn ? 'white' : 'rgba(255,255,255,0.4)',
                  }}
                >
                  SMS varsler
                </span>
              </div>
            </div>

            {/* Privacy line */}
            <p
              style={{
                fontFamily: 'Nunito Sans, sans-serif',
                fontWeight: 300,
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.35)',
                lineHeight: '1.5',
                marginTop: '-8px',
              }}
            >
              Vi sender deg maskinnyheter, programvareoppdateringer og viktige kontovarsler på SMS og e-post. Avslutt når som helst i innstillinger.
            </p>

            <button type="submit" className="btn-full-white" style={{ marginTop: '4px' }} disabled={isLoading}>
              {isLoading ? 'Oppretter konto...' : 'Opprett konto'}
            </button>
            <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
              Har du allerede en konto?{' '}
              <button
                type="button"
                onClick={() => { setTab('login'); setErrorMsg(''); }}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit', fontSize: 'inherit' }}
              >
                Logg inn
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}